import React, { useState, useCallback, useMemo } from 'react';
import WeeklyHeader from './WeeklyHeader';
import useSelectedSensor from '../../hooks/useSelectedSensor';
import useSensorOptions from '../../hooks/useSensorOptions';
import useCompanyOptions from '../../hooks/useCompanyOptions';
import useSelectedCompany from '../../hooks/useSelectedCompany';
import WeeklyContent from './WeeklyContent';
import { Box, Grid, Paper, useTheme, useMediaQuery } from '@mui/material';
import { useWeeklyDataQuery, useAdultnChildWeeklyDataQuery, useGenderWeeklyDataQuery, useGetAddonByDeviceQuery } from '../../services/api';
import { useSelector } from 'react-redux';
import WeeklyFilterBox from './WeeklyFilterBox';
import WeeklyLabel from './WeeklyLabel';
import useGroupOptions from '../../hooks/useGroupOptions';
import useSelectedGroup from '../../hooks/useSelectedGroup';

const Weekly = () => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { company, role } = useSelector((state) => state.global);
    const isSuperOrPowerAdmin = role === 'Super Admin' || role === 'Power Admin';

    // Company, Group and Device options
    const { companyOptions } = useCompanyOptions();
    const { selectedCompany, selectedCompanyId, handleCompanyChange } = useSelectedCompany({ companyOptions });
    const groupOptions = useGroupOptions(isSuperOrPowerAdmin ? selectedCompanyId : company);
    const sensorOptions = useSensorOptions(isSuperOrPowerAdmin ? selectedCompanyId : company);
    const { selectedGroup, handleGroupChange } = useSelectedGroup(groupOptions, isSuperOrPowerAdmin ? selectedCompanyId : company);
    const filteredSensorOptions = useMemo(() => {
        if (!groupOptions.length) {
            return sensorOptions;
        }

        const selectedGroupObj = groupOptions.find((group) => group._id === selectedGroup);
        const selectedDeviceIds = selectedGroupObj?.deviceIds || [];

        return sensorOptions.filter((sensor) => selectedDeviceIds.includes(sensor._id));
    }, [sensorOptions, groupOptions, selectedGroup]);
    const { selectedSensor, handleSensorChange, getSelectedSensorLabel } = useSelectedSensor(filteredSensorOptions, isSuperOrPowerAdmin ? selectedCompany : company);

    const [showLabels, setShowLabels] = useState(true);
    const [tabValue, setTabValue] = useState(0);
    const [selectedMonthYear, setSelectedMonthYear] = useState({
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
    });

    // API
    const { data: addonData } = useGetAddonByDeviceQuery(selectedSensor, { skip: !selectedSensor });
    const {
        data: weeklyData,
        isLoading: weeklyLoading,
        error: weeklyError,
    } = useWeeklyDataQuery({ mac: selectedSensor, month: selectedMonthYear.month, year: selectedMonthYear.year }, { skip: tabValue !== 0 || !selectedSensor });

    const {
        data: adultChildData,
        isLoading: adultChildLoading,
        error: adultChildError,
    } = useAdultnChildWeeklyDataQuery({ mac: selectedSensor, month: selectedMonthYear.month, year: selectedMonthYear.year }, { skip: tabValue !== 1 || !selectedSensor });

    const {
        data: genderData,
        isLoading: genderLoading,
        error: genderError,
    } = useGenderWeeklyDataQuery({ mac: selectedSensor, month: selectedMonthYear.month, year: selectedMonthYear.year }, { skip: tabValue !== 2 || !selectedSensor });

    // Determine which data to use based on the selected tab
    const processedData = tabValue === 0 ? weeklyData : tabValue === 1 ? adultChildData : tabValue === 2 ? genderData : null;
    const chartIsLoading = tabValue === 0 ? weeklyLoading : tabValue === 1 ? adultChildLoading : tabValue === 2 ? genderLoading : null;
    const error = tabValue === 0 ? weeklyError : tabValue === 1 ? adultChildError : tabValue === 2 ? genderError : null;

    const handleShowLabelsChange = useCallback((event) => {
        setShowLabels(event.target.checked);
    }, []);

    const handleTabChange = (newValue) => {
        setTabValue(newValue);
    };

    return (
        <Box m={isSmallScreen ? '0.5rem' : '1rem 2.5rem'}>
            {isSmallScreen ? (
                <Grid container spacing={2} paddingBottom={3}>
                    <Grid item xs={12}>
                        <WeeklyHeader isSmallScreen={isSmallScreen} selectedMonthYear={selectedMonthYear} />
                        <WeeklyLabel sensorLabel={getSelectedSensorLabel()} isSmallScreen={isSmallScreen} tabValue={tabValue} onTabChange={handleTabChange} addonData={addonData} />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Paper
                            sx={{
                                position: 'sticky',
                                top: 20,
                                height: 'auto',
                                overflow: 'auto',
                                borderRadius: '15px',
                                boxShadow: '8px 8px 20px rgba(0, 0, 0, 0.1)',
                            }}
                        >
                            <WeeklyFilterBox
                                sensorLabel={getSelectedSensorLabel()}
                                isSmallScreen={isSmallScreen}
                                showLabels={showLabels}
                                onShowLabelsChange={handleShowLabelsChange}
                                sensorOptions={filteredSensorOptions}
                                selectedSensor={selectedSensor}
                                handleSensorChange={handleSensorChange}
                                companyOptions={companyOptions}
                                selectedCompany={isSuperOrPowerAdmin ? selectedCompany : null}
                                handleCompanyChange={isSuperOrPowerAdmin ? handleCompanyChange : null}
                                isSuperAdmin={isSuperOrPowerAdmin}
                                selectedMonthYear={selectedMonthYear}
                                setSelectedMonthYear={setSelectedMonthYear}
                                selectedGroup={selectedGroup}
                                handleGroupChange={handleGroupChange}
                                groupOptions={groupOptions}
                            />
                        </Paper>
                    </Grid>
                    <Grid item xs={12}>
                        <WeeklyContent
                            selectedSensor={selectedSensor}
                            showLabels={showLabels}
                            processedData={processedData}
                            selectedMonth={selectedMonthYear.month}
                            selectedYear={selectedMonthYear.year}
                            error={error}
                            isLoading={chartIsLoading}
                            isSmallScreen={isSmallScreen}
                            sensorLabel={getSelectedSensorLabel()}
                            tabValue={tabValue}
                        />
                    </Grid>
                </Grid>
            ) : (
                <Grid container spacing={2} paddingBottom={3}>
                    <Grid item xs={12} md={10}>
                        <WeeklyHeader isSmallScreen={isSmallScreen} selectedMonthYear={selectedMonthYear} />
                        <WeeklyLabel sensorLabel={getSelectedSensorLabel()} isSmallScreen={isSmallScreen} tabValue={tabValue} onTabChange={handleTabChange} addonData={addonData} />
                        <WeeklyContent
                            selectedSensor={selectedSensor}
                            showLabels={showLabels}
                            processedData={processedData}
                            selectedMonth={selectedMonthYear.month}
                            selectedYear={selectedMonthYear.year}
                            error={error}
                            isLoading={chartIsLoading}
                            isSmallScreen={isSmallScreen}
                            sensorLabel={getSelectedSensorLabel()}
                            tabValue={tabValue}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Paper
                            sx={{
                                position: 'sticky',
                                top: 20,
                                height: 'auto',
                                overflow: 'auto',
                                borderRadius: '15px',
                                boxShadow: '8px 8px 20px rgba(0, 0, 0, 0.1)',
                            }}
                        >
                            <WeeklyFilterBox
                                sensorLabel={getSelectedSensorLabel()}
                                isSmallScreen={isSmallScreen}
                                showLabels={showLabels}
                                onShowLabelsChange={handleShowLabelsChange}
                                sensorOptions={filteredSensorOptions}
                                selectedSensor={selectedSensor}
                                handleSensorChange={handleSensorChange}
                                companyOptions={companyOptions}
                                selectedCompany={isSuperOrPowerAdmin ? selectedCompany : null}
                                handleCompanyChange={isSuperOrPowerAdmin ? handleCompanyChange : null}
                                isSuperAdmin={isSuperOrPowerAdmin}
                                selectedMonthYear={selectedMonthYear}
                                setSelectedMonthYear={setSelectedMonthYear}
                                selectedGroup={selectedGroup}
                                handleGroupChange={handleGroupChange}
                                groupOptions={groupOptions}
                            />
                        </Paper>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
};

export default Weekly;
